import { useRef, useState } from "react";

import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faFilter, faThumbtack } from "@fortawesome/pro-regular-svg-icons";
import { faFilter as faFilterSolid } from "@fortawesome/pro-solid-svg-icons";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import { AnimatePresence } from "framer-motion";
import { useOnClickOutside } from "usehooks-ts";

import {
  DateRange,
  DateRangePickerProps,
} from "src/base-components/DateRangePicker";
import { FilterFormData } from "src/base-components/FilterPopover/types";
import { MotionDropdownPanel } from "src/base-components/MotionDropdownPanel";
import { DropdownItem } from "src/design-system/Dropdown";
import { Icon } from "src/design-system/Icon";
import { EventsFilterPopover } from "src/eventsCatalogue/EventsFilterPopover";
import { EVENT_FALLBACK_ICON } from "src/eventsCatalogue/utils";
import { useWorkspaceFeatureGates } from "src/hooks/useWorkspaceFeatureGates";

export type FilterMode = "all" | "pinned" | "advanced_filters";

type OpenMode = "dropdown" | "advanced_filters" | false;

type RelatedEventsFilterDropdownProps = {
  displayName: string;
  hasPinnedEvents: boolean;
  eventType: string;
  schemaIcon?: IconProp;
  filterMode: FilterMode;
  onFilterModeChange: (mode: FilterMode) => void;
  onCustomFiltersOpenChange?: (isOpen: boolean) => void;
  onApply: (formData: FilterFormData) => void;
  dateRangeValue?: DateRange;
  onDateRangeChange?: DateRangePickerProps["onChange"];
  defaultValues?: FilterFormData;
  hasFilterCondition?: boolean;
};

type FilterOption =
  | { key: FilterMode; value: string; icon: IconProp; disabled: boolean }
  | { key: "divider"; isDivider: true };

export const RelatedEventsFilterDropdown: React.FC<
  RelatedEventsFilterDropdownProps
> = ({
  displayName,
  hasPinnedEvents,
  eventType,
  schemaIcon,
  filterMode,
  onFilterModeChange,
  onCustomFiltersOpenChange,
  onApply,
  dateRangeValue,
  onDateRangeChange,
  defaultValues,
  hasFilterCondition,
}) => {
  const { featuresEventsEnabled } = useWorkspaceFeatureGates();
  const [openMode, setOpenModeRaw] = useState<OpenMode>(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useOnClickOutside([containerRef, dropdownRef], () => {
    if (openMode === "dropdown") {
      setOpenMode(false);
    }
  });

  const setOpenMode = (mode: OpenMode) => {
    setOpenModeRaw(mode);
    onCustomFiltersOpenChange?.(mode === "advanced_filters");
  };

  const isFilterActive =
    filterMode === "pinned" || !!hasFilterCondition || openMode !== false;

  const allIcon = schemaIcon ?? EVENT_FALLBACK_ICON;

  const filterOptions: FilterOption[] = [
    { key: "all", value: `All ${displayName}`, icon: allIcon, disabled: false },
    {
      key: "pinned",
      value: `Pinned ${displayName}`,
      icon: faThumbtack,
      disabled: !hasPinnedEvents,
    },
    ...(featuresEventsEnabled
      ? [
          { key: "divider" as const, isDivider: true as const },
          {
            key: "advanced_filters" as const,
            value: "Apply advanced filters",
            icon: faFilter,
            disabled: false,
          },
        ]
      : []),
  ];

  const handleButtonClick = () => {
    if (openMode !== false) {
      setOpenMode(false);
    } else if (filterMode === "advanced_filters" || hasFilterCondition) {
      setOpenMode("advanced_filters");
    } else {
      setOpenMode("dropdown");
    }
  };

  const handleModeSelect = (mode: FilterMode) => {
    if (mode === "advanced_filters") {
      setOpenMode("advanced_filters");
    } else {
      onFilterModeChange(mode);
      setOpenMode(false);
    }
  };

  const handleClickBack = () => {
    setOpenMode("dropdown");
  };

  return (
    <>
      <Listbox
        ref={containerRef}
        as="div"
        className="relative"
        value={filterMode}
        onChange={handleModeSelect}
      >
        <ListboxButton
          className="flex items-center gap-x-1 rounded-lg border-none px-2 py-1.5 focus:outline-none focus:ring-0"
          data-loc="events-filter-dropdown-button"
          onClick={handleButtonClick}
        >
          <Icon
            icon={isFilterActive ? faFilterSolid : faFilter}
            size="xs"
            variant={isFilterActive ? "active" : "default"}
          />
        </ListboxButton>
        <AnimatePresence>
          {openMode === "dropdown" && (
            <ListboxOptions
              ref={dropdownRef}
              anchor={{ to: "bottom end", gap: 8 }}
              as={MotionDropdownPanel}
              data-loc="events-filter-dropdown"
              static
            >
              <div className="flex h-12 items-center px-4 py-2.5">
                <span className="font-inter-semibold-13px text-gray-800">
                  Show {displayName}
                </span>
              </div>
              {filterOptions.map((option) => {
                if ("isDivider" in option) {
                  return (
                    <hr
                      key={option.key}
                      className="my-2 h-px border-none bg-gray-200"
                    />
                  );
                }
                return (
                  <ListboxOption
                    key={option.key}
                    as={DropdownItem}
                    disabled={option.disabled}
                    value={option.key}
                  >
                    <div className="flex items-center gap-x-2">
                      <Icon
                        icon={option.icon}
                        size="xs"
                        variant={option.disabled ? "disabled" : "default"}
                      />
                      <span>{option.value}</span>
                    </div>
                  </ListboxOption>
                );
              })}
            </ListboxOptions>
          )}
        </AnimatePresence>
      </Listbox>
      {featuresEventsEnabled && (
        <EventsFilterPopover
          button={<div />}
          dateRangeValue={dateRangeValue}
          defaultValues={defaultValues}
          eventType={eventType}
          hasActiveFilter={hasFilterCondition}
          isOpen={openMode === "advanced_filters"}
          onApply={onApply}
          onClickBack={handleClickBack}
          onDateRangeChange={onDateRangeChange}
          onOpenChange={(isOpen) =>
            setOpenMode(isOpen ? "advanced_filters" : false)
          }
        />
      )}
    </>
  );
};
